import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

function FullScreenLoader() {
  return (
    <div
      className="min-h-screen flex items-center justify-center text-sm text-gray-500"
      data-testid="auth-loading"
    >
      Loading&hellip;
    </div>
  );
}

/**
 * Gate for the whole authenticated app. Nothing under it renders until the
 * backend has validated the JWT via GET /auth/me.
 */
export default function ProtectedRoute({ children }) {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) return <FullScreenLoader />;
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  // Defence in depth: the API already refuses unverified sessions, but a
  // stale token from before verification existed must not reach the dashboard.
  if (user?.email_verified === false) {
    return (
      <Navigate
        to="/verify-email"
        replace
        state={{ email: user.email || "" }}
      />
    );
  }
  return children;
}

/** Gate for admin-only routes. Bounces non-admins to the dashboard. */
export function AdminRoute({ children }) {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  const location = useLocation();

  if (loading) return <FullScreenLoader />;
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (!isAdmin) return <Navigate to="/app" replace />;
  return children;
}

/** Keeps signed-in users off /login and /register. */
export function PublicOnlyRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) return <FullScreenLoader />;
  if (isAuthenticated) return <Navigate to="/app" replace />;
  return children;
}